/**
 * Pure view logic for the Pending tab (kanban 43160d8b): which follow-ups
 * are still open, how they group (due this week / later / fired and not yet
 * seen), what each row says, and which follow-ups belong on an item's
 * workspace. Kept out of the components so the grouping and the words are
 * testable without a DOM (repo convention: logic in .ts, components in .tsx).
 *
 * Jess (2026-10-01): "a pending bucket of things that I'm expecting Merlin
 * to give me answers on in the future."
 */
import {
  OPEN_FOLLOWUP_STATUSES,
  type FollowupStatus,
  type MerlinFollowupRead,
} from '../../services/merlinFollowups';
import type { DoorThreadRead } from '../../services/qcpInvestigations';

const DAY_MS = 86_400_000;

/* ---------------------------------------------------------------- */
/* Which rows                                                        */
/* ---------------------------------------------------------------- */

export function isOpenFollowup(row: Pick<MerlinFollowupRead, 'status'>): boolean {
  return (OPEN_FOLLOWUP_STATUSES as readonly string[]).includes(row.status);
}

/** The follow-ups still to come: the tab's count pill. */
export function openFollowups(rows: MerlinFollowupRead[]): MerlinFollowupRead[] {
  return rows.filter(isOpenFollowup);
}

/**
 * The follow-ups that hang on this item: anchored to the thread itself, or
 * promised in the thread's own Merlin conversation.
 */
export function followupsForThread(
  rows: MerlinFollowupRead[],
  thread: Pick<DoorThreadRead, 'id' | 'conversation_id'>,
): MerlinFollowupRead[] {
  return rows.filter(
    (r) =>
      (r.anchor_kind === 'signal_thread' && r.anchor_id === thread.id) ||
      (!!thread.conversation_id && r.conversation_id === thread.conversation_id),
  );
}

/* ---------------------------------------------------------------- */
/* Seen marks for fired rows                                         */
/* ---------------------------------------------------------------- */

/**
 * A fired (or expired, or answered) follow-up is "not yet seen" until the
 * reader opens it here. The mark lives in localStorage like the thread
 * seen-marks (threadTriageView), keyed by follow-up id: the backend has no
 * per-reader read state for a promise, and the bell row's own read state is
 * not visible to this tab.
 */
const SEEN_KEY = 'qcp.followups.seen';

export type FollowupSeenMap = Record<string, string>;

function readSeen(): FollowupSeenMap {
  try {
    const raw = localStorage.getItem(SEEN_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === 'object' ? (parsed as FollowupSeenMap) : {};
  } catch {
    return {};
  }
}

export function followupSeenSnapshot(): FollowupSeenMap {
  return readSeen();
}

/** The reader looked at this settled follow-up: its resolved time is the mark. */
export function markFollowupSeen(row: Pick<MerlinFollowupRead, 'id' | 'resolved_at' | 'updated_at'>): void {
  try {
    const seen = readSeen();
    seen[row.id] = row.resolved_at ?? row.updated_at;
    localStorage.setItem(SEEN_KEY, JSON.stringify(seen));
  } catch {
    /* storage disabled — the row just stays "new", the safe way to be wrong */
  }
}

/** Settled by Merlin (fired / expired / satisfied) and not opened here since. */
export function isSettledUnseen(
  row: Pick<MerlinFollowupRead, 'id' | 'status' | 'resolved_at' | 'updated_at'>,
  seen: FollowupSeenMap,
): boolean {
  if (!isSettledByMerlin(row)) return false;
  const mark = seen[row.id];
  if (!mark) return true;
  const seenAt = new Date(mark).getTime();
  const settledAt = new Date(row.resolved_at ?? row.updated_at).getTime();
  if (Number.isNaN(seenAt) || Number.isNaN(settledAt)) return true;
  return settledAt > seenAt;
}

/** Fired, expired or satisfied: Merlin closed it, with a note to read. A
 *  cancelled one was the reader's own doing and needs no look. */
export function isSettledByMerlin(row: Pick<MerlinFollowupRead, 'status'>): boolean {
  return row.status === 'fired' || row.status === 'expired' || row.status === 'satisfied';
}

/* ---------------------------------------------------------------- */
/* Groups                                                            */
/* ---------------------------------------------------------------- */

export interface PendingGroups {
  /** Settled by Merlin and not yet opened here: the answers waiting to be read. */
  firedUnseen: MerlinFollowupRead[];
  /** Open, due within the next seven days (or overdue, or with no date: a
   *  condition Merlin checks daily). Soonest first. */
  dueThisWeek: MerlinFollowupRead[];
  /** Open, due later than seven days out. Soonest first. */
  later: MerlinFollowupRead[];
  /** Everything else, newest first: read already, cancelled, expired. */
  settled: MerlinFollowupRead[];
}

export const WEEK_MS = 7 * DAY_MS;

function dueTime(row: Pick<MerlinFollowupRead, 'due_at'>): number {
  const t = row.due_at ? new Date(row.due_at).getTime() : NaN;
  return Number.isNaN(t) ? Number.POSITIVE_INFINITY : t;
}

/** Open rows soonest first; a row with no date (watch until it changes,
 *  checked daily) sorts with this week's, after the dated ones. */
function bySoonest(a: MerlinFollowupRead, b: MerlinFollowupRead): number {
  return dueTime(a) - dueTime(b) || a.promised_at.localeCompare(b.promised_at);
}

function byNewestSettled(a: MerlinFollowupRead, b: MerlinFollowupRead): number {
  const at = (r: MerlinFollowupRead) => r.resolved_at ?? r.updated_at;
  return at(b).localeCompare(at(a));
}

export function groupPending(
  rows: MerlinFollowupRead[],
  seen: FollowupSeenMap,
  now: Date = new Date(),
): PendingGroups {
  const firedUnseen: MerlinFollowupRead[] = [];
  const dueThisWeek: MerlinFollowupRead[] = [];
  const later: MerlinFollowupRead[] = [];
  const settled: MerlinFollowupRead[] = [];
  const weekEnd = now.getTime() + WEEK_MS;
  for (const row of rows) {
    if (isOpenFollowup(row)) {
      // No date = a condition Merlin re-checks daily: it could land any day,
      // so it is this week's, not "later".
      if (!row.due_at || dueTime(row) <= weekEnd) dueThisWeek.push(row);
      else later.push(row);
    } else if (isSettledUnseen(row, seen)) {
      firedUnseen.push(row);
    } else {
      settled.push(row);
    }
  }
  return {
    firedUnseen: firedUnseen.sort(byNewestSettled),
    dueThisWeek: dueThisWeek.sort(bySoonest),
    later: later.sort(bySoonest),
    settled: settled.sort(byNewestSettled),
  };
}

/* ---------------------------------------------------------------- */
/* Words                                                             */
/* ---------------------------------------------------------------- */

export const STATUS_WORD: Record<FollowupStatus, string> = {
  scheduled: 'Scheduled',
  checking: 'Checking',
  fired: 'Answered',
  satisfied: 'Answered',
  expired: 'Expired',
  cancelled: 'Cancelled',
};

/** The row's badge: where the promise stands, in one word. */
export function statusWord(row: Pick<MerlinFollowupRead, 'status' | 'failed_check_count'>): string {
  if (row.status === 'fired' && row.failed_check_count >= 3) return 'Could not check';
  return STATUS_WORD[row.status] ?? row.status;
}

const SHORT_DATE: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };

/** "Oct 6", or "Oct 6, 2027" when it is not this year. */
export function shortDate(iso: string, now: Date = new Date()): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const opts: Intl.DateTimeFormatOptions =
    d.getFullYear() === now.getFullYear() ? SHORT_DATE : { ...SHORT_DATE, year: 'numeric' };
  return d.toLocaleDateString(undefined, opts);
}

/** Whole calendar days from today to the date, in local time (negative = past). */
export function daysUntil(iso: string, now: Date = new Date()): number | null {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const startOf = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  return Math.round((startOf(d) - startOf(now)) / DAY_MS);
}

/**
 * When Merlin comes back, as the row and the item's line say it:
 * "today", "tomorrow", "Mon Oct 6", "3 days overdue", or for a condition
 * with no date "checks daily until …".
 */
export function dueWord(row: Pick<MerlinFollowupRead, 'kind' | 'due_at' | 'expires_at' | 'status'>, now: Date = new Date()): string {
  if (!isOpenFollowup(row)) return '';
  if (!row.due_at) {
    const until = shortDate(row.expires_at, now);
    return until ? `checks daily until ${until}` : 'checks daily';
  }
  const days = daysUntil(row.due_at, now);
  if (days === null) return '';
  if (row.status === 'checking') return 'checking now';
  if (days === 0) return 'due today';
  if (days === 1) return 'due tomorrow';
  if (days < 0) return `${-days} day${days === -1 ? '' : 's'} overdue`;
  const weekday = new Date(row.due_at).toLocaleDateString(undefined, { weekday: 'short' });
  return `due ${weekday} ${shortDate(row.due_at, now)}`;
}

/**
 * Who asked for it: Jess in her chat on the door, Merlin's own plan from
 * the nightly run, or the promise audit that loaded the older ones.
 */
export function askedByWord(
  row: Pick<MerlinFollowupRead, 'profile_id' | 'origin' | 'owner_name'>,
  viewerName: string | null,
): string {
  const owner = row.owner_name && row.owner_name !== viewerName ? row.owner_name : 'you';
  if (row.origin === 'backfill') return `promised to ${owner} · loaded from the promise audit`;
  if (row.profile_id === 'thread_investigator') return "Merlin's own plan from its nightly pass";
  return `promised to ${owner} in chat`;
}

/** "Merlin will check back on Oct 6: <promise>" — the item's inline line. */
export function inlinePromiseLine(row: MerlinFollowupRead, now: Date = new Date()): string {
  const when = dueWord(row, now);
  const promise = row.promise_text.trim();
  if (row.kind === 'date' && row.due_at) {
    const prefix =
      row.status === 'checking'
        ? 'Merlin is checking now'
        : `Merlin will check back ${when.replace(/^due /, '')}`;
    return promise ? `${prefix}: ${promise}` : prefix;
  }
  const prefix = `Merlin is watching this (${when})`;
  return promise ? `${prefix}: ${promise}` : prefix;
}

/** The settled line under a row: what Merlin found, and when. */
export function settledLine(row: MerlinFollowupRead, now: Date = new Date()): string {
  const when = row.resolved_at ? shortDate(row.resolved_at, now) : '';
  const note = (row.resolution_note ?? '').trim();
  const verb =
    row.status === 'cancelled'
      ? 'Cancelled'
      : row.status === 'expired'
        ? 'Window closed'
        : row.status === 'fired' && row.failed_check_count >= 3
          ? 'Merlin could not complete the check'
          : 'Merlin answered';
  const head = when ? `${verb} ${when}` : verb;
  return note ? `${head}: ${note}` : head;
}

/** How many settled-by-Merlin rows wait to be read: the tab's amber notice. */
export function unseenAnswerCount(rows: MerlinFollowupRead[], seen: FollowupSeenMap): number {
  return rows.filter((r) => isSettledUnseen(r, seen)).length;
}
